import { useEffect, useState, memo } from "react"
import { useDispatch } from "react-redux"
import { AppDispatch } from "state/store"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { useTranslation } from "react-i18next"
import { CustomInput, ProductWrite, ProductImage, InputMaxLength, FormStatus } from "types"
import TextInput from "components/inputs/textInput"
import ToggleInput from "components/inputs/toggleInput"
import NumberInput from "components/inputs/numberInput"
import TextAreaInput from "components/inputs/textareaInput"
import MultipleFileInput from "components/inputs/multipleFileInput"
import Section from "components/layout/section"
import CustomInputs from "../categories/customInputs/customInputs"
import useProducts from "hooks/useProducts"
import useStorages from "hooks/useStorages"
import useCategories from "hooks/useCategories"
import useLocations from "hooks/useLocations"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { logger } from "utils/secureLogger"
import CategorySelector from "components/inputs/categorySelector"
import LocationSelector from "components/inputs/locationSelector"
import { ReactComponent as SaveIcon } from "assets/icons/save.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import ProcessingButton from "components/inputs/processingButton"
import InputGroup from "@/components/inputs/InputGroup"
import UserSelector from "@/components/searchAndSelect/UserSelector"
import BarcodeListInput from "@/components/inputs/barcodeListInput"

interface EditProductFormProps {
    storageId: string,
    productId: string
}

const EditProductForm: React.FC<EditProductFormProps> = ({ storageId, productId }) => {

    const { t } = useTranslation()

    // Form productsStatus.
    const [errors, setErrors] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [sku, setSku] = useState("")
    const [name, setName] = useState("")
    const [description, setDescription] = useState(``)
    const [unit, setUnit] = useState(``)
    const [allowNegativeInventory, setAllowNegativeInventory] = useState(false)
    const [lowStockWarning, setLowStockWarning] = useState<number | undefined>(undefined)
    const [files, setFiles] = useState<File[]>([])
    const [existingImages, setExistingImages] = useState<ProductImage[]>([])
    const [imagesToDelete, setImagesToDelete] = useState<string[]>([])

    const [selectedCategories, setSelectedCategories] = useState<string[]>([])
    const [selectedLocations, setSelectedLocations] = useState<string[]>([])
    const [customInputs, setCustomInputs] = useState<CustomInput>({})

    // Responsible user state
    const [responsibleUserId, setResponsibleUserId] = useState<string>("")
    const [barcodes, setBarcodes] = useState<string[]>([])

    // Redux hooks.
    const dispatch = useDispatch<AppDispatch>()

    // Layout hooks.
    const { addToast } = useLayout()

    const {
        product,
        productsStatus,
        updateProductAsync,
        isUpdating
    } = useProducts({ storageId, productId })

    const {
        storage,
        isSharedAccessEnabled
    } = useStorages({ storageId })

    const {
        categories,
        allCategories,
        categoriesStatus
    } = useCategories({ storageId })

    const {
        locations,
        allLocations,
        locationsStatus
    } = useLocations({ storageId })

    useEffect(() => {
        if (!product) return
        setSku(product.sku)
        setName(product.name)
        setDescription(product.description)
        setUnit(product.unit || storage?.settings?.defaultProductUnit || '')
        setAllowNegativeInventory(product.stockSettings?.allowNegativeInventory ?? false)
        setLowStockWarning(product.stockSettings?.lowStockWarning)
        setSelectedCategories(product.categories)
        setSelectedLocations(product.locations || [])
        setCustomInputs(product.customInputs as CustomInput)
        setExistingImages(product.images || [])
        setResponsibleUserId(product.stockSettings?.responsibleUserId || "")
        setBarcodes(product.barcodes?.length ? product.barcodes : [])
    }, [product, storage])

    // Handle form submit.
    const handleSubmit = async (e: React.FormEvent, saveAsDraft: boolean = false) => {
        e.preventDefault();
        setErrors([]);
        setIsSubmitting(true);

        // Validate responsible user
        if (!responsibleUserId) {
            setErrors([t('errorResponsibleUserRequired')]);
            addToast({ text: t('errorResponsibleUserRequired'), colorScheme: 'red' });
            setIsSubmitting(false);
            return;
        }

        try {
            const productData = {
                ids: {
                    storageId,
                    productId,
                },
                sku,
                name,
                description,
                unit,
                categories: selectedCategories,
                locations: selectedLocations,
                stockSettings: {
                    allowNegativeInventory,
                    lowStockWarning,
                    responsibleUserId,
                },
                customInputs,
                isDraft: saveAsDraft,
                barcodes: barcodes.filter((code) => code.trim() !== ""),
                images: {
                    files,
                    existingImages,
                    imagesToDelete,
                },
            } as ProductWrite;

            const result = await updateProductAsync(productData);
            logger.info('Product update successful', { productId }, 'UpdateProduct');
            addToast({ text: t('toastProductUpdated'), colorScheme: 'green' });
            setFiles([]);
            setImagesToDelete([]);
        } catch (error: unknown) {
            logger.error('Failed to update product', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            setErrors([errorMessage]);
            addToast({ text: t('toastProductError'), colorScheme: 'red' });
        } finally {
            setIsSubmitting(false);
        }
    } 

    // Handle SKU change.
    const handleSkuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSku(e.target.value)
    }

    // Hanlde name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle description change.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    const handleUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUnit(e.target.value)
    }

    // Handle allow negative inventory change.
    const handleAllowNegativeInventoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setAllowNegativeInventory(e.target.checked)
    }

    // Handle low stock warning change.
    const handleLowStockWarningChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLowStockWarning(Number(e.target.value) > 0 ? Number(e.target.value) : undefined)
    }

    // Handle existing image deletion
    const handleExistingImageDelete = (imageId: string) => {
        setImagesToDelete([...imagesToDelete, imageId])
        setExistingImages(existingImages.filter(img => img.productImageId !== imageId))
    }

    // Handle image reordering
    const handleImageReorder = (reorderedImages: ProductImage[]) => {
        setExistingImages(reorderedImages)
    }



    // Catch missing ID or failed loading storage.
    if (!storageId) return <Callout type={'error'}>{t('storageIdMissing')}</Callout>
    if (!productId) return <Callout type={'error'}>{t('productId')}</Callout>
    if (productsStatus === FormStatus.LOADING || categoriesStatus === FormStatus.LOADING || locationsStatus === FormStatus.LOADING) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingCategories')}</Callout>

    // Print the form.
    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isSubmitting}
        >
            {product?.isDraft && (
                <Section>
                    <Callout type="info" id="editProductInfo">
                        {t('editProductInfo', 'This product is a draft. You can publish it or keep it as a draft. Once published, it cannot be saved as a draft again.')}
                    </Callout>
                </Section>
            )}
            <Section title={t('productImages')}>
                <MultipleFileInput
                    label=""
                    files={files}
                    existingImages={existingImages}
                    onFilesChange={setFiles}
                    onExistingImageDelete={handleExistingImageDelete}
                    onImageReorder={handleImageReorder}
                    onFilesReorder={setFiles}
                />
            </Section>
            <Section title={t('sectionProductDetails')}>
                <InputGroup>
                    <TextInput id={`id`} label={t('labelSKU')} required onChange={handleSkuChange} value={sku} maxLength={InputMaxLength.short} />
                    <TextInput id={`name`} label={t('labelProductName')} required onChange={handleNameChange} value={name} maxLength={InputMaxLength.short} />
                </InputGroup>
                <TextAreaInput label={t('description')} onChange={handleDescriptionChange} value={description} maxLength={InputMaxLength.long} />
                <TextInput id={`unit`} label={t('unit')} required onChange={handleUnitChange} value={unit} maxLength={InputMaxLength.short} />
            </Section>
            <Section title={t('labelBarcodes')}>
                <BarcodeListInput barcodes={barcodes} onChange={setBarcodes} />
            </Section>
            <Section title={t('sectionStock')}>
                <ToggleInput label={t('allowNegativeInventory')} checked={allowNegativeInventory} onChange={handleAllowNegativeInventoryChange} />
                <NumberInput label={t('lowStockWarning')} value={lowStockWarning || 0} onChange={handleLowStockWarningChange} />
                {isSharedAccessEnabled() && <UserSelector
                    storageId={storageId}
                    value={responsibleUserId}
                    onChange={setResponsibleUserId}
                    label={t('labelResponsibleUser')}
                    required={true}
                />}
            </Section>
            <Section title={t('sectionCategoryAndLocation')}>
                <div className={`input-holder-wide`}>
                    <label>{t('category')}</label>
                    <CategorySelector
                        categories={categories}
                        allCategories={allCategories}
                        selectedCategories={selectedCategories}
                        setSelectedCategories={setSelectedCategories}
                    />
                </div>
                <div className={`input-holder-wide`}>
                    <label>{t('location')}</label>
                    <LocationSelector
                        locations={locations}
                        allLocations={allLocations}
                        selectedLocations={selectedLocations}
                        setSelectedLocations={setSelectedLocations}
                    />
                </div>
            </Section>
            <CustomInputs storageId={storageId} categoryIds={selectedCategories} customInputs={customInputs} setCustomInputs={setCustomInputs} categories={allCategories} />
            <Section>
                <ButtonGroup>
                    {isSubmitting ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <>
                            {product?.isDraft && (
                                <Button wideOnMobile 
                                    type="button"
                                    design="white" 
                                    onClick={() => handleSubmit({ preventDefault: () => {} } as React.FormEvent, true)}
                                >
                                    <SaveIcon />
                                    <span>{t('buttonSaveAsDraft', 'Save as Draft')}</span>
                                </Button>
                            )}
                            <Button wideOnMobile type="submit" design="primary">
                                <SaveIcon />
                                <span>{product?.isDraft ? t('buttonPublish', 'Publish') : t('buttonSaveChanges')}</span>
                            </Button>
                        </>
                    )}
                </ButtonGroup>
            </Section>
            {errors.length > 0 && <FormErrors errors={errors} />}
        </Form>
    )
}

export default memo(EditProductForm)



